import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Building2,
  Users,
  Percent,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  LineChart,
  PieChart,
  Target,
  Loader2,
  Calendar,
  RefreshCw,
  FileText,
  Bell,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import InsightsPanel from '../InsightsPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Date ranges
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  
  // Data states
  const [recommendations, setRecommendations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  
  // Report data
  const [occupancyReport, setOccupancyReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  
  // Loading states
  const [generatingForecasts, setGeneratingForecasts] = useState(false);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);

  useEffect(() => {
    if (currentProperty?.id) {
      loadDashboard();
      loadRecommendations();
      loadAlerts();
    }
  }, [currentProperty]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/analytics/dashboard?property_id=${currentProperty.id}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecommendations = async () => {
    try {
      const response = await api.get(`/analytics/recommendations?property_id=${currentProperty.id}&status=pending`);
      setRecommendations(response.data || []);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await api.get(`/analytics/alerts?property_id=${currentProperty.id}&status=active`);
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadForecasts = async () => {
    try {
      const today = new Date();
      const start = format(today, 'yyyy-MM-dd');
      const end = format(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
      
      const response = await api.get(`/analytics/forecasts?property_id=${currentProperty.id}&start_date=${start}&end_date=${end}&forecast_type=occupancy`);
      setForecasts(response.data || []);
    } catch (error) {
      console.error('Error loading forecasts:', error);
    }
  };

  const handleGenerateForecasts = async () => {
    try {
      setGeneratingForecasts(true);
      const today = format(new Date(), 'yyyy-MM-dd');
      await api.post(`/analytics/forecasts/generate?property_id=${currentProperty.id}&start_date=${today}&days=30`);
      toast.success('Forecasts generated successfully');
      loadForecasts();
    } catch (error) {
      toast.error('Failed to generate forecasts');
    } finally {
      setGeneratingForecasts(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    try {
      setGeneratingRecommendations(true);
      const today = format(new Date(), 'yyyy-MM-dd');
      await api.post(`/analytics/recommendations/generate?property_id=${currentProperty.id}&start_date=${today}&days=14`);
      toast.success('Recommendations generated successfully');
      loadRecommendations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to generate recommendations');
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const handleAcceptRecommendation = async (id) => {
    try {
      await api.post(`/analytics/recommendations/${id}/accept`);
      toast.success('Recommendation accepted');
      loadRecommendations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to accept recommendation');
    }
  };

  const handleRejectRecommendation = async (id) => {
    try {
      await api.post(`/analytics/recommendations/${id}/reject?reason=Manual%20override`);
      toast.success('Recommendation rejected');
      loadRecommendations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to reject recommendation');
    }
  };

  const handleAcknowledgeAlert = async (id) => {
    try {
      await api.post(`/analytics/alerts/${id}/acknowledge`);
      toast.success('Alert acknowledged');
      loadAlerts();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getChangeIndicator = (value) => {
    if (value > 0) {
      return <ArrowUpRight className="h-4 w-4 text-green-500" />;
    } else if (value < 0) {
      return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getAlertSeverityBadge = (severity) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', label: 'Critical' },
      high: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', label: 'High' },
      medium: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', label: 'Medium' },
      low: { color: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300', label: 'Low' }
    };
    const c = config[severity] || config.medium;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property to view analytics</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics & Revenue</h1>
          <p className="text-muted-foreground">Forecasting, optimization, and performance insights</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleGenerateForecasts}
            disabled={generatingForecasts}
            data-testid="generate-forecasts-btn"
          >
            {generatingForecasts ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LineChart className="mr-2 h-4 w-4" />
            )}
            Generate Forecasts
          </Button>
          <Button 
            onClick={handleGenerateRecommendations}
            disabled={generatingRecommendations}
            data-testid="generate-recommendations-btn"
          >
            {generatingRecommendations ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lightbulb className="mr-2 h-4 w-4" />
            )}
            Get Recommendations
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Insights &amp; Forecast</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="tab-recommendations">
            Recommendations
            {recommendations.length > 0 && (
              <Badge variant="secondary" className="ml-2">{recommendations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alerts
            {alerts.length > 0 && (
              <Badge variant="destructive" className="ml-2">{alerts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Insights & Forecast Tab (no-AI: statistical forecast + rule-based recommendations) */}
        <TabsContent value="insights" className="space-y-6">
          <InsightsPanel propertyId={currentProperty?.id} />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Today's KPIs */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-occupancy">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today&apos;s Occupancy</CardTitle>
                <Percent className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(dashboard?.today?.occupancy)}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboard?.today?.rooms_sold || 0} rooms sold
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-adr">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">ADR</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(dashboard?.today?.adr)}</div>
                <p className="text-xs text-muted-foreground">Average Daily Rate</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-revpar">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">RevPAR</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(dashboard?.today?.revpar)}</div>
                <p className="text-xs text-muted-foreground">Revenue Per Available Room</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-revenue">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today&apos;s Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(dashboard?.today?.total_revenue)}</div>
                <p className="text-xs text-muted-foreground">Total revenue today</p>
              </CardContent>
            </Card>
          </div>

          {/* MTD Performance & Budget */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Month-to-Date Performance</CardTitle>
                <CardDescription>
                  {dashboard?.mtd?.days_reported || 0} days reported
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Room Nights Sold</span>
                  <span className="font-semibold">{dashboard?.mtd?.rooms_sold || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Room Revenue</span>
                  <span className="font-semibold">{formatCurrency(dashboard?.mtd?.room_revenue)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Revenue</span>
                  <span className="font-semibold">{formatCurrency(dashboard?.mtd?.total_revenue)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Average Occupancy</span>
                  <span className="font-semibold">{formatPercent(dashboard?.mtd?.avg_occupancy)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Average ADR</span>
                  <span className="font-semibold">{formatCurrency(dashboard?.mtd?.adr)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Budget vs Actual</CardTitle>
                <CardDescription>Current month performance against target</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {dashboard?.budget?.target_revenue ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Revenue Target</span>
                        <span className="font-medium">{formatCurrency(dashboard?.budget?.target_revenue)}</span>
                      </div>
                      <Progress 
                        value={(dashboard?.budget?.actual_revenue / dashboard?.budget?.target_revenue) * 100} 
                        className="h-2"
                      />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Actual: {formatCurrency(dashboard?.budget?.actual_revenue)}</span>
                        <span className={dashboard?.budget?.variance >= 0 ? "text-green-600" : "text-red-600"}>
                          {dashboard?.budget?.variance >= 0 ? '+' : ''}{formatCurrency(dashboard?.budget?.variance)}
                        </span>
                      </div>
                    </div>
                    {dashboard?.budget?.occupancy_target && (
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-muted-foreground">Occupancy Target</span>
                        <span className="font-medium">{formatPercent(dashboard?.budget?.occupancy_target)}</span>
                      </div>
                    )}
                    {dashboard?.budget?.adr_target && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">ADR Target</span>
                        <span className="font-medium">{formatCurrency(dashboard?.budget?.adr_target)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No budget set for this month</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Pending Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                    <Lightbulb className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboard?.pending_recommendations || 0}</div>
                    <p className="text-xs text-muted-foreground">Rate suggestions to review</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                    dashboard?.alerts?.critical > 0 
                      ? 'bg-red-100 dark:bg-red-900' 
                      : 'bg-blue-100 dark:bg-blue-900'
                  }`}>
                    <Bell className={`h-6 w-6 ${
                      dashboard?.alerts?.critical > 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-blue-600 dark:text-blue-400'
                    }`} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboard?.alerts?.total || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard?.alerts?.critical > 0 
                        ? `${dashboard.alerts.critical} critical` 
                        : 'No critical alerts'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Today&apos;s Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {(dashboard?.today?.arrivals || 0) + (dashboard?.today?.departures || 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard?.today?.arrivals || 0} arrivals, {dashboard?.today?.departures || 0} departures
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 7-Day Trend */}
          {dashboard?.trend?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>7-Day Trend</CardTitle>
                <CardDescription>Daily performance over the past week</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Occupancy</TableHead>
                      <TableHead className="text-right">ADR</TableHead>
                      <TableHead className="text-right">RevPAR</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.trend.map((day) => (
                      <TableRow key={day.stat_date}>
                        <TableCell>{format(new Date(day.stat_date), 'EEE, MMM d')}</TableCell>
                        <TableCell className="text-right">{formatPercent(day.occupancy_percent)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.adr)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.revpar)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.total_revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Rate Recommendations</CardTitle>
                  <CardDescription>AI-powered pricing suggestions based on demand forecasts</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {recommendations.length > 0 ? (
                <div className="space-y-4">
                  {recommendations.map((rec) => (
                    <div key={rec.id} className="p-4 border rounded-lg" data-testid={`recommendation-${rec.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {format(new Date(rec.recommendation_date), 'EEE, MMM d, yyyy')}
                            </span>
                            <Badge className={
                              rec.recommendation_type === 'increase' 
                                ? 'bg-green-100 text-green-800' 
                                : rec.recommendation_type === 'decrease'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-slate-100 text-slate-800'
                            }>
                              {rec.recommendation_type === 'increase' ? (
                                <TrendingUp className="h-3 w-3 mr-1" />
                              ) : rec.recommendation_type === 'decrease' ? (
                                <TrendingDown className="h-3 w-3 mr-1" />
                              ) : null}
                              {rec.recommendation_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{rec.reasoning}</p>
                          <div className="flex items-center gap-4 text-sm mt-2">
                            <span>
                              Current: <strong>{formatCurrency(rec.current_rate)}</strong>
                            </span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            <span>
                              Recommended: <strong className={
                                rec.rate_change_percent > 0 ? 'text-green-600' : rec.rate_change_percent < 0 ? 'text-red-600' : ''
                              }>{formatCurrency(rec.recommended_rate)}</strong>
                              <span className="text-muted-foreground ml-1">
                                ({rec.rate_change_percent > 0 ? '+' : ''}{rec.rate_change_percent?.toFixed(1)}%)
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRejectRecommendation(rec.id)}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleAcceptRecommendation(rec.id)}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            Accept
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No pending recommendations</p>
                  <Button 
                    variant="link" 
                    onClick={handleGenerateRecommendations}
                    className="mt-2"
                  >
                    Generate recommendations
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Alerts</CardTitle>
              <CardDescription>Important notifications requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-4">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="p-4 border rounded-lg" data-testid={`alert-${alert.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className={`h-4 w-4 ${
                              alert.severity === 'critical' ? 'text-red-500' :
                              alert.severity === 'high' ? 'text-orange-500' :
                              'text-yellow-500'
                            }`} />
                            <span className="font-medium">{alert.title}</span>
                            {getAlertSeverityBadge(alert.severity)}
                          </div>
                          {alert.description && (
                            <p className="text-sm text-muted-foreground">{alert.description}</p>
                          )}
                          {alert.recommended_action && (
                            <p className="text-sm text-primary">
                              Recommended: {alert.recommended_action}
                            </p>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleAcknowledgeAlert(alert.id)}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
                  <p>No active alerts</p>
                  <p className="text-sm mt-1">All systems operating normally</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
